import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LayoutDashboard, CalendarCheck, MessageSquare, Heart, Receipt, UserRound, Settings, LogOut } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { useAccountData } from "@/hooks/useAccountData";
import { Avatar } from "@/components/Avatar";
import { ModeSwitcher } from "@/components/ModeSwitcher";
import { ReviewDialog } from "@/components/ReviewDialog";
import { CustomerDashboard } from "@/components/account/CustomerDashboard";
import { CustomerTrips } from "@/components/account/CustomerTrips";
import { CustomerMessages } from "@/components/account/CustomerMessages";
import { CustomerProfile } from "@/components/account/CustomerProfile";
import { CustomerSettings } from "@/components/account/CustomerSettings";
import { CustomerReceipts } from "@/components/account/CustomerReceipts";
import { CustomerSaved } from "@/components/account/CustomerSaved";
import { Seo } from "@/components/Seo";

const NAV = [
  ["dashboard", LayoutDashboard],
  ["trips", CalendarCheck],
  ["messages", MessageSquare],
  ["saved", Heart],
  ["receipts", Receipt],
  ["profile", UserRound],
  ["settings", Settings],
];

export default function Account() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const data = useAccountData();
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get("tab") || "dashboard");
  const [reviewTrip, setReviewTrip] = useState(null);
  const [activeThread, setActiveThread] = useState(null);

  const pendingIds = new Set((data.pending || []).map((p) => p.booking_id));
  const goMessages = (bid) => { setActiveThread(bid); setTab("messages"); };
  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div data-testid="account-page" className="bg-slate-50 min-h-screen pt-20">
      <Seo noindex title="My Account | Belo Rentals" path="/account" />
      <div className="flex max-w-7xl mx-auto">
        <aside className="hidden md:flex flex-col w-64 shrink-0 border-r border-slate-200 bg-white min-h-[calc(100vh-5rem)] sticky top-20 self-start py-6 px-3">
          {user?.is_host && (
            <div className="px-2 pb-4 mb-3 border-b border-slate-100">
              <ModeSwitcher className="w-full justify-between" />
            </div>
          )}
          <div className="flex items-center gap-3 px-3 pb-5 mb-3 border-b border-slate-100">
            <Avatar name={user?.name} size="md" accent />
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 text-sm truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="space-y-1 flex-1">
            {NAV.map(([k, Icon]) => {
              const badge = k === "trips" ? pendingIds.size : 0;
              return (
                <button key={k} onClick={() => setTab(k)} data-testid={`nav-${k}`}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${tab === k ? "bg-blue-50 text-blue-700 font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
                  <Icon size={18} /> <span className="flex-1 text-left">{t(`account.${k}`)}</span>
                  {badge > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700">{badge}</span>}
                </button>
              );
            })}
          </div>
          <button onClick={doLogout} data-testid="account-logout" className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-slate-500 hover:bg-slate-50 mt-2">
            <LogOut size={18} /> {t("account.signOut")}
          </button>
        </aside>

        <main className="flex-1 min-w-0 px-5 sm:px-8 py-8">
          {user?.is_host && <div className="md:hidden mb-4"><ModeSwitcher size="lg" /></div>}
          <div className="md:hidden flex gap-2 overflow-x-auto pb-3 mb-3 -mx-1 px-1">
            {NAV.map(([k, Icon]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`mnav-${k}`}
                className={`shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${tab === k ? "bg-blue-600 text-white" : "bg-white text-slate-600 shadow-card"}`}>
                <Icon size={15} /> {t(`account.${k}`)}
              </button>
            ))}
          </div>

          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-6" data-testid="account-section-title">{t(`account.${tab}`)}</h1>

          {tab === "dashboard" && <CustomerDashboard bookings={data.bookings} pending={data.pending} onGoTrips={() => setTab("trips")} onReview={setReviewTrip} />}
          {tab === "trips" && <CustomerTrips bookings={data.bookings} pendingIds={pendingIds} onReview={setReviewTrip} onGoMessages={goMessages} onRefresh={data.load} />}
          {tab === "messages" && <CustomerMessages conversations={data.conversations} active={activeThread} setActive={setActiveThread} />}
          {tab === "saved" && <CustomerSaved favorites={data.favorites} onChange={data.load} />}
          {tab === "receipts" && <CustomerReceipts bookings={data.bookings} />}
          {tab === "profile" && <CustomerProfile me={data.profile} onSaved={data.load} />}
          {tab === "settings" && <CustomerSettings me={data.profile} onSaved={data.load} />}
        </main>
      </div>

      {reviewTrip && <ReviewDialog trip={reviewTrip} counterpartyLabel={reviewTrip.counterparty || t("review.theHost")} onClose={() => setReviewTrip(null)} onDone={data.load} />}
    </div>
  );
}
